import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  Grid,
  Alert,
  Chip,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import { validateCustomerUid } from '../../utils/customerWorkflowUtils';
import { logger } from '../../utils/logger';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import RefreshIcon from '@mui/icons-material/Refresh';
import AppService from '../../services/AppService';

/**
 * Enhanced component for entering and validating a customer UID in workflow requests
 * Provides improved validation, error handling, and customer information display
 */
const WorkflowRequestUidSection = ({
  customerUid,
  onCustomerUidChange,
  onCustomerUidValidate,
  validationError: externalValidationError,
  validationSuccess: externalValidationSuccess,
  customerInfo: externalCustomerInfo,
  disabled,
  token,
  onCustomerDataLoaded
}) => {
  const [inputUid, setInputUid] = useState(customerUid || '');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(externalValidationError || '');
  const [validationSuccess, setValidationSuccess] = useState(externalValidationSuccess || false);
  const [customerInfo, setCustomerInfo] = useState(externalCustomerInfo || null);
  const [fullCustomerData, setFullCustomerData] = useState(null);
  
  // Sync with external props when they change
  useEffect(() => {
    if (externalValidationError !== undefined) {
      setValidationError(externalValidationError);
    }
    if (externalValidationSuccess !== undefined) {
      setValidationSuccess(externalValidationSuccess);
    }
    if (externalCustomerInfo) {
      setCustomerInfo(externalCustomerInfo);
    }
    if (customerUid && customerUid !== inputUid) {
      setInputUid(customerUid);
    }
  }, [externalValidationError, externalValidationSuccess, externalCustomerInfo, customerUid]);

  // Handle UID input change
  const handleUidChange = (e) => {
    const newUid = e.target.value;
    setInputUid(newUid);
    
    // Reset validation state when UID changes
    if (validationSuccess) {
      setValidationSuccess(false);
      setCustomerInfo(null);
    }
    
    if (validationError) {
      setValidationError('');
    }
    
    // Notify parent component
    if (onCustomerUidChange) {
      onCustomerUidChange(newUid);
    }
  };

  // Validate customer UID
  const handleValidateClick = async () => {
    if (!inputUid) {
      setValidationError('Customer UID is required');
      return;
    }
    
    setIsValidating(true);
    setValidationError('');
    
    try {
      // Use our enhanced validation utility
      const validationResult = await validateCustomerUid(inputUid, async (uid) => {
        // Make API call to validate customer
        return await AppService.get("/api/customers/lookup", { 
          params: { uid },
          headers: { Authorization: `Bearer ${token}` }
        });
      });
      
      if (validationResult.isValid) {
        // Validation successful
        setValidationSuccess(true);
        setCustomerInfo(validationResult.details.customerInfo);
        setFullCustomerData(validationResult.details.customerData);
        
        // Notify parent components
        if (onCustomerUidValidate) {
          onCustomerUidValidate(inputUid, validationResult.details.customerData);
        }
        
        if (onCustomerDataLoaded) {
          onCustomerDataLoaded(validationResult.details.customerData);
        }
        
        logger.debug('Customer validated successfully', { 
          uid: inputUid, 
          customerInfo: validationResult.details.customerInfo 
        });
      } else {
        // Validation failed
        setValidationSuccess(false);
        setValidationError(validationResult.error);
        setCustomerInfo(null);
        setFullCustomerData(null);
        
        logger.warn('Customer validation failed', { 
          uid: inputUid, 
          error: validationResult.error 
        });
      }
    } catch (error) {
      // Handle unexpected errors
      setValidationSuccess(false);
      setValidationError('An error occurred during validation. Please try again.');
      setCustomerInfo(null);
      setFullCustomerData(null);
      
      logger.error('Error during customer validation', { uid: inputUid, error });
    } finally {
      setIsValidating(false);
    }
  };

  // Handle Enter key press for validation
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && inputUid && !isValidating && !validationSuccess && !disabled) {
      handleValidateClick();
    }
  };

  // Reset validation state
  const handleReset = () => {
    setInputUid('');
    setValidationSuccess(false);
    setValidationError('');
    setCustomerInfo(null);
    setFullCustomerData(null);
    
    // Notify parent component
    if (onCustomerUidChange) {
      onCustomerUidChange('');
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">
            Customer Identification
          </Typography>
          {validationSuccess && (
            <Chip 
              color="success" 
              size="small" 
              label="Validated" 
              icon={<BadgeIcon />} 
            />
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Customer UID"
              variant="outlined"
              value={inputUid}
              onChange={handleUidChange}
              onKeyPress={handleKeyPress}
              disabled={disabled || isValidating || validationSuccess}
              error={!!validationError}
              helperText={validationError || ''}
              placeholder="Enter customer UID"
              size="small"
              InputProps={{
                endAdornment: inputUid && !validationSuccess && !isValidating ? (
                  <Tooltip title="Reset">
                    <IconButton
                      size="small"
                      onClick={handleReset}
                      edge="end"
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleValidateClick}
              disabled={!inputUid || disabled || isValidating || validationSuccess}
              sx={{ height: '40px' }}
              startIcon={isValidating ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isValidating ? 'Validating...' : 'Validate'}
            </Button>
            {validationSuccess && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleReset}
                sx={{ height: '40px', ml: 1 }}
              >
                Change Customer
              </Button>
            )}
          </Grid>
        </Grid>
        
        {validationError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {validationError}
          </Alert>
        )}
        
        {validationSuccess && customerInfo && (
          <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Customer Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" mb={1}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">Name</Typography>
                    <Typography variant="body1">{customerInfo.name}</Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" mb={1}>
                  <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">Organization</Typography>
                    <Typography variant="body1">{customerInfo.organization}</Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" mb={1}>
                  <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">Email</Typography>
                    <Typography variant="body1">{customerInfo.email}</Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" mb={1}>
                  <PhoneIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">Phone</Typography>
                    <Typography variant="body1">{customerInfo.phone}</Typography>
                  </Box>
                </Box>
              </Grid>
              
              {customerInfo.documentCount > 0 && (
                <Grid item xs={12}>
                  <Alert severity="info" icon={false}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2">
                        {customerInfo.documentCount} document(s) available from customer profile
                      </Typography>
                    </Box>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}
      </CardContent>
    </Card>
  );
};

WorkflowRequestUidSection.propTypes = {
  customerUid: PropTypes.string,
  onCustomerUidChange: PropTypes.func,
  onCustomerUidValidate: PropTypes.func,
  validationError: PropTypes.string,
  validationSuccess: PropTypes.bool,
  customerInfo: PropTypes.object,
  disabled: PropTypes.bool,
  token: PropTypes.string,
  onCustomerDataLoaded: PropTypes.func
};

WorkflowRequestUidSection.defaultProps = {
  customerUid: '',
  validationSuccess: false,
  disabled: false
};

export default WorkflowRequestUidSection;